"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Settings, ArrowLeft } from "lucide-react";
import { getQuickFilterFields, renderFilterDropdowns } from "../utils/filter-rendering";
import { partitionFilterState } from "../utils/filter-rendering-helpers";
import { getDatetimePresets } from "@/lib/utils/formatting/datetime-presets";
import { AdvancedFilterPanel } from "./AdvancedFilterPanel";
import type { SimpleTableConfig, BaseEntity, FieldValue } from "../types";
import type { FilterState } from "@/components/shared/types";

export interface QuickFiltersAdvancedProps {
  advancedFilters:
    | Record<string, unknown>
    | Array<{ id: string; field: string; operator: string; value: unknown }>;
  filterOperator: "AND" | "OR";
  setFilterOperator: (operator: "AND" | "OR") => void;
  addAdvancedFilter: (fieldId: string, filterType: string, value: FieldValue) => void;
  removeAdvancedFilter: (fieldId: string) => void;
  clearAllAdvancedFilters: () => void;
}

interface QuickFiltersPanelProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  simpleFilters: Record<string, unknown>;
  onFiltersChange: (newFilters: Record<string, unknown>) => void;
  onCustomRangeOpen: (fieldId: string, fieldLabel: string) => void;
  activeFilterCount: number;
  advancedFilterCount: number;
  /** Controlled mode — the surface (popover / mobile sheet) owns the toggle. */
  mode: "quick" | "advanced";
  onModeChange: (mode: "quick" | "advanced") => void;
  advancedFilterProps?: QuickFiltersAdvancedProps;
  /** Extra classes for the quick-filter grid container (mobile uses one column). */
  gridClassName?: string;
}

/**
 * Quick / Advanced filter panel body.
 *
 * Single source of truth for WHICH filters exist and HOW they render, shared by
 * the desktop `QuickFiltersPopover` and the mobile filter sheet. Both surfaces
 * must expose the same filter set — see `getQuickFilterFields`.
 */
export function QuickFiltersPanel<TData extends BaseEntity>({
  config,
  simpleFilters,
  onFiltersChange,
  onCustomRangeOpen,
  activeFilterCount,
  advancedFilterCount,
  mode,
  onModeChange,
  advancedFilterProps,
  gridClassName = "grid-cols-1 sm:grid-cols-2",
}: QuickFiltersPanelProps<TData>) {
  const quickFilterFields = getQuickFilterFields<TData>(config);
  const labelById = new Map(quickFilterFields.map((f) => [f.id, f.label]));

  const hasQuickFilters = quickFilterFields.length > 0;

  const quickFilterConfig = {
    ...config,
    features: { ...config.features, filters: true },
    fields: Object.fromEntries(quickFilterFields.map((f) => [f.id, f.fieldConfig])),
  } as SimpleTableConfig<BaseEntity>;

  // Values the inputs can't represent (FilterContext stores `unknown`) are kept
  // aside and merged back, so editing one field never drops another's filter.
  const { state: filterState, extras } = partitionFilterState(simpleFilters);
  const handleFilterStateChange = (next: FilterState) => onFiltersChange({ ...extras, ...next });

  const filterElements = hasQuickFilters
    ? (renderFilterDropdowns(
        quickFilterConfig,
        filterState,
        handleFilterStateChange,
        getDatetimePresets,
        onCustomRangeOpen,
        true
      ) as React.ReactNode[])
    : [];
  const elements = Array.isArray(filterElements) ? filterElements : [filterElements];

  if (mode === "advanced" && advancedFilterProps) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onModeChange("quick")}
            className="text-xs -ml-2"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            Quick Filters
          </Button>
          <h3 className="font-semibold text-sm">Advanced Filters</h3>
        </div>
        <AdvancedFilterPanel<TData>
          showAdvancedFilters={true}
          config={config}
          advancedFilters={advancedFilterProps.advancedFilters}
          filterOperator={advancedFilterProps.filterOperator}
          setFilterOperator={advancedFilterProps.setFilterOperator}
          addAdvancedFilter={advancedFilterProps.addAdvancedFilter}
          removeAdvancedFilter={advancedFilterProps.removeAdvancedFilter}
          clearAllAdvancedFilters={advancedFilterProps.clearAllAdvancedFilters}
          hideHeader
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm">Quick Filters</h3>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount} active
            </Badge>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange({})}
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </Button>
        )}
      </div>

      <div
        className={`grid ${gridClassName} gap-x-4 gap-y-3 pb-2 min-h-0`}
        style={{ scrollbarGutter: "stable" }}
      >
        {elements.filter(React.isValidElement).map((el) => {
          const fieldId = String((el as React.ReactElement).key ?? "");
          const label = labelById.get(fieldId);
          return (
            <div key={fieldId} className="flex min-w-0 flex-col gap-1 overflow-visible">
              {label && (
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {label}
                </span>
              )}
              <div className="min-w-0">{el}</div>
            </div>
          );
        })}
      </div>

      {advancedFilterProps && (
        <div className="flex justify-end pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onModeChange("advanced")}
            className="text-xs"
          >
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Advanced Filters
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 min-w-[18px] px-1">
                {advancedFilterCount}
              </Badge>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
